import type { AudienceSegment } from './types.js';
import { classifyAudienceSegment } from './audience-labels.js';

/**
 * The fixed icon vocabulary for an audience persona card's size badge
 * (audience_profiles slide). The segment worker picks one through its forced
 * output schema; the web maps each key to a Phosphor icon
 * (web/src/lib/audienceIcons.ts). Adding a key here means adding its icon
 * there too — the web map is typed against this list.
 */
export const AUDIENCE_ICONS = [
  'car',
  'home',
  'briefcase',
  'office',
  'storefront',
  'shopping',
  'dining',
  'health',
  'medical',
  'dental',
  'fitness',
  'beauty',
  'education',
  'family',
  'pets',
  'travel',
  'entertainment',
  'finance',
  'money',
  'legal',
  'home_services',
  'construction',
  'outdoors',
  'local',
  'research',
  'people',
] as const;

export type AudienceIcon = (typeof AUDIENCE_ICONS)[number];

/** Model-facing one-liner per key, so the schema description says what each icon means. */
export const AUDIENCE_ICON_HINTS: Record<AudienceIcon, string> = {
  car: 'vehicles, auto shopping, auto loans',
  home: 'homeowners, home buyers, mortgages',
  briefcase: 'professionals, careers, jobs',
  office: 'businesses, companies, B2B buyers',
  storefront: 'small business owners, local merchants',
  shopping: 'retail shoppers',
  dining: 'restaurants, food, drink',
  health: 'health and wellness',
  medical: 'patients, doctors, clinics',
  dental: 'dental care',
  fitness: 'gyms, sports, active lifestyles',
  beauty: 'salons, spas, cosmetics',
  education: 'students, schools, continuing education',
  family: 'parents, kids, families',
  pets: 'pet owners',
  travel: 'travel, tourism, hotels',
  entertainment: 'events, tickets, attractions',
  finance: 'banking, credit, investing',
  money: 'price-sensitive buyers, deals, savings',
  legal: 'legal services',
  home_services: 'repairs, HVAC, plumbing, home improvement',
  construction: 'contractors, builders, trades',
  outdoors: 'outdoor recreation, nature, gardening',
  local: 'nearby residents, commuters, visitors',
  research: 'people actively researching a purchase',
  people: 'general consumers',
};

export function isAudienceIcon(value: unknown): value is AudienceIcon {
  return typeof value === 'string' && (AUDIENCE_ICONS as readonly string[]).includes(value);
}

/**
 * The icon a card shows: the model's pick when it made a valid one, otherwise
 * one derived from how the segment was built (older proposals predate the
 * field). Never empty, so every card's size row has its badge.
 */
export function audienceIcon(segment: AudienceSegment): AudienceIcon {
  if (isAudienceIcon(segment.icon)) return segment.icon;
  const labels = classifyAudienceSegment(segment);
  if (labels.includes('b2b')) return 'office';
  if (labels[0] === 'intent' || segment.mode === 'intent') return 'research';
  return 'people';
}
